import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";

const certificates = [
  { slug: "iso-9001", name: "ISO 9001:2015", img: "/assets/certs/iso.png", pdf: "/assets/certs/iso.pdf" },
  { slug: "udyam", name: "Udyam Registration", img: "/assets/certs/udyam.png", pdf: "/assets/certs/udyam.pdf" },
  { slug: "gst", name: "GST Registration", img: "/assets/certs/gst.png", pdf: "/assets/certs/gst.pdf" },
];

const tileReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.05 * i },
  }),
};

const CertLightbox = ({ cert, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="cert-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md sm:p-8"
    >
      <motion.div
        data-lenis-prevent
        data-testid="cert-lightbox-panel"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-white/10 bg-[#111111] text-white shadow-2xl"
      >
        <button
          data-testid="cert-lightbox-close"
          onClick={onClose}
          aria-label="Close certificate preview"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white transition-colors duration-200 hover:bg-brand"
        >
          <X size={16} />
        </button>

        <div className="flex items-center justify-center bg-white p-4 sm:p-6">
          <img
            src={cert.img}
            alt={cert.name}
            data-testid="cert-lightbox-image"
            className="max-h-[62vh] w-auto object-contain"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-8">
          <h3
            data-testid="cert-lightbox-title"
            className="font-heading text-xl font-black tracking-tight"
          >
            {cert.name}
          </h3>
          <a
            href={cert.pdf}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cert-lightbox-pdf"
            className="inline-flex items-center gap-2 border border-white/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-brand hover:bg-brand"
          >
            Original PDF
            <ArrowUpRight size={13} />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const CertificationsSection = () => {
  const [activeCert, setActiveCert] = useState(null);

  return (
    <>
      <div data-testid="certs-section">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
          <h2 className="flex items-center gap-3 font-heading text-xl font-bold tracking-tight sm:text-2xl">
            <span className="inline-block h-2 w-2 bg-brand" />
            Certifications & Registrations
          </h2>
          <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/40">
            3 documents
          </span>
        </div>

        <div className="mt-6 grid max-w-[260px] grid-cols-1 gap-4 sm:max-w-[960px] sm:grid-cols-3 sm:gap-5">
          {certificates.map((cert, i) => (
            <motion.button
              key={cert.slug}
              custom={i}
              variants={tileReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              data-testid={`cert-card-${cert.slug}`}
              onClick={() => setActiveCert(cert)}
              aria-label={`View ${cert.name} certificate`}
              className="group text-center"
            >
              <span className="block aspect-[3/4] w-full overflow-hidden border border-white/10 bg-white p-3 transition-colors duration-300 group-hover:border-white/30 sm:p-4">
                <img
                  src={cert.img}
                  alt={cert.name}
                  loading="lazy"
                  className="h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                />
              </span>
              <span className="mt-3 block font-heading text-sm font-bold tracking-tight transition-colors duration-300 group-hover:text-brand">
                {cert.name}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeCert && <CertLightbox cert={activeCert} onClose={() => setActiveCert(null)} />}
      </AnimatePresence>
    </>
  );
};
